import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { InfoRow } from './InfoRow';
import {
  getCategoryName,
  getSubCategoryName,
  getEntryPermissionLabel,
  formatDateWithTime,
} from '@/utils/residents/maintenance';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { SEMANTIC_COLORS } from '@/constants/residents/residentColors';

interface WorkRequestInfoSectionProps {
  ticket: IMaintenanceListResponse;
  variant?: 'mobile' | 'desktop';
}

/**
 * WorkRequestInfoSection - Displays work request information table
 * Used in both mobile and desktop detail views
 */
export const WorkRequestInfoSection: React.FC<WorkRequestInfoSectionProps> = ({
  ticket,
  variant = 'mobile'
}) => {
  const category = getCategoryName(ticket);
  const subCategory = getSubCategoryName(ticket);
  const entryPermission = getEntryPermissionLabel(ticket);

  return (
    <View style={variant === 'mobile' ? styles.mobileSection : styles.desktopSection}>
      <Text style={variant === 'mobile' ? styles.mobileSectionTitle : styles.desktopSectionTitle}>
        Work request information
      </Text>
      
      <View style={styles.infoTable}>
        <InfoRow 
          label="ID" 
          value={String(ticket.maintenanceRequestId || '-')} 
          variant={variant} 
        />
        <InfoRow 
          label="Submitted" 
          value={formatDateWithTime(ticket.createdOn)} 
          variant={variant} 
        />
        <InfoRow 
          label="Category" 
          value={category} 
          variant={variant} 
        />
        <InfoRow 
          label="Subcategory" 
          value={subCategory} 
          variant={variant} 
        />
        <InfoRow 
          label="Entry permission" 
          value={entryPermission} 
          variant={variant} 
        />
        {ticket.updatedOn && (
          <InfoRow 
            label="Last updated" 
            value={formatDateWithTime(ticket.updatedOn)} 
            variant={variant} 
          />
        )}
        {ticket.completedOn && (
          <InfoRow 
            label="Completed" 
            value={formatDateWithTime(ticket.completedOn)} 
            variant={variant} 
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  mobileSection: {
    gap: 16,
  },
  desktopSection: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 16,
  },
  infoTable: {
    gap: 12,
  },
  mobileSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  desktopSectionTitle: {
    fontSize: 16,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 24,
  },
});
